import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowPathIcon,
  EyeIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  PlayIcon,
  XCircleIcon,
  QueueListIcon,
} from '@heroicons/react/24/outline';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Spinner from '@/components/ui/Spinner';
import EmptyState from '@/components/ui/EmptyState';
import {
  TableContainer, Table, TableHead, TableBody, TableRow, TableHeaderCell, TableCell,
} from '@/components/ui/Table';
import { useApi } from '@/hooks/useApi';
import type { ConnectorJobsListResponse, ConnectorJobStatus } from '@/types/api';

function getStatusBadge(status: ConnectorJobStatus) {
  switch (status) {
    case 'succeeded':
      return <Badge variant="success">Succeeded</Badge>;
    case 'failed':
      return <Badge variant="danger">Failed</Badge>;
    case 'running':
      return <Badge variant="primary">Running</Badge>;
    case 'queued':
      return <Badge variant="warning">Queued</Badge>;
    default:
      return <Badge variant="slate">{status}</Badge>;
  }
}

function getStatusIcon(status: ConnectorJobStatus) {
  switch (status) {
    case 'succeeded':
      return <CheckCircleIcon className="w-4 h-4 text-success-500" />;
    case 'failed':
      return <XCircleIcon className="w-4 h-4 text-danger-500" />;
    case 'running':
      return <PlayIcon className="w-4 h-4 text-primary-500" />;
    case 'queued':
      return <ClockIcon className="w-4 h-4 text-warning-500" />;
    default:
      return <QueueListIcon className="w-4 h-4 text-slate-400" />;
  }
}

function getJobTypeBadge(jobType: string) {
  switch (jobType) {
    case 'push':
      return <Badge variant="primary">Push</Badge>;
    case 'push_dry_run':
      return <Badge variant="secondary">Dry Run</Badge>;
    case 'feed_rebuild':
      return <Badge variant="slate">Feed Rebuild</Badge>;
    default:
      return <Badge variant="slate">{jobType}</Badge>;
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString();
}

export default function DistDeliveriesPage() {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Build API endpoint with filters
  const apiEndpoint = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    if (statusFilter) {
      params.set('status', statusFilter);
    }
    return `admin/connector-jobs?${params.toString()}`;
  }, [page, statusFilter]);

  // Fetch connector jobs (deliveries) from API
  const { data, isLoading, error, refetch } = useApi<ConnectorJobsListResponse>(apiEndpoint);

  const jobs = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / pageSize);

  // Calculate summary counts
  const statusCounts = useMemo(() => {
    return {
      queued: jobs.filter(j => j.connector_job_status === 'queued').length,
      running: jobs.filter(j => j.connector_job_status === 'running').length,
      succeeded: jobs.filter(j => j.connector_job_status === 'succeeded').length,
      failed: jobs.filter(j => j.connector_job_status === 'failed').length,
    };
  }, [jobs]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Deliveries</h1>
          <p className="text-slate-600 dark:text-slate-300 mt-1">Track book deliveries to distribution platforms</p>
        </div>
        <Button
          variant="outline"
          leftIcon={<ArrowPathIcon className="w-4 h-4" />}
          onClick={() => refetch()}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </div>

      {/* Status Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card padding="md" className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter('queued'); setPage(1); }}>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-warning-100 dark:bg-warning-900/30 rounded-lg">
              <ClockIcon className="w-5 h-5 text-warning-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{statusCounts.queued}</p>
              <p className="text-sm text-slate-600 dark:text-slate-300">Queued</p>
            </div>
          </div>
        </Card>
        <Card padding="md" className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter('running'); setPage(1); }}>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <PlayIcon className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{statusCounts.running}</p>
              <p className="text-sm text-slate-600 dark:text-slate-300">Running</p>
            </div>
          </div>
        </Card>
        <Card padding="md" className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter('succeeded'); setPage(1); }}>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-success-100 dark:bg-success-900/30 rounded-lg">
              <CheckCircleIcon className="w-5 h-5 text-success-600" />
          </div>
          <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{statusCounts.succeeded}</p>
              <p className="text-sm text-slate-600 dark:text-slate-300">Succeeded</p>
            </div>
          </div>
        </Card>
        <Card padding="md" className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter('failed'); setPage(1); }}>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-danger-100 dark:bg-danger-900/30 rounded-lg">
              <XCircleIcon className="w-5 h-5 text-danger-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{statusCounts.failed}</p>
              <p className="text-sm text-slate-600 dark:text-slate-300">Failed</p>
          </div>
        </div>
        </Card>
      </div>

      {/* Filters */}
      <Card padding="md">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-900/30 focus:border-primary-500"
            >
              <option value="">All Statuses</option>
              <option value="queued">Queued</option>
              <option value="running">Running</option>
              <option value="succeeded">Succeeded</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          {statusFilter && (
            <Button variant="ghost" size="sm" onClick={() => { setStatusFilter(''); setPage(1); }}>
              Clear Filters
            </Button>
          )}
          <div className="ml-auto text-sm text-slate-600 dark:text-slate-300">
            Showing {jobs.length} of {total} deliveries
          </div>
        </div>
      </Card>

      {/* Error State */}
      {error && (
        <Card padding="lg">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-danger-500 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">Failed to load deliveries</h3>
            <p className="text-slate-600 dark:text-slate-300 mb-4">{error}</p>
            <Button onClick={() => refetch()} leftIcon={<ArrowPathIcon className="w-4 h-4" />}>
              Try Again
            </Button>
          </div>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && !data && (
        <Card padding="lg">
          <div className="flex flex-col items-center justify-center py-12">
            <Spinner size="lg" />
            <p className="text-slate-600 dark:text-slate-300 mt-4">Loading deliveries...</p>
          </div>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && !error && jobs.length === 0 && (
        <EmptyState
          icon={<TruckIcon className="w-12 h-12" />}
          title="No deliveries found"
          description={statusFilter 
            ? `No deliveries with status "${statusFilter}" found. Try clearing the filter.`
            : "Deliveries will appear here when you push books to distribution platforms"
          }
          action={statusFilter ? (
            <Button onClick={() => { setStatusFilter(''); setPage(1); }}>
              Clear Filter
            </Button>
          ) : undefined}
        />
      )}

      {/* Deliveries Table */}
      {!error && jobs.length > 0 && (
        <Card padding="none">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow isHoverable={false}>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Book ID</TableHeaderCell>
                  <TableHeaderCell>Distributor</TableHeaderCell>
                  <TableHeaderCell>Type</TableHeaderCell>
                  <TableHeaderCell>Attempts</TableHeaderCell>
                  <TableHeaderCell>Started</TableHeaderCell>
                  <TableHeaderCell>Completed</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(job.connector_job_status)}
                        {getStatusBadge(job.connector_job_status)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Link 
                        to={`/distribution/books/${job.delivery_id}`}
                        className="text-sm font-mono text-primary-600 hover:text-primary-700 hover:underline"
                      >
                        {job.delivery_id.slice(0, 8)}...
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link 
                        to={`/distribution/platforms/${job.distributor_id}`}
                        className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
                      >
                        {job.distributor_id.slice(0, 8)}...
                      </Link>
                    </TableCell>
                    <TableCell>{getJobTypeBadge(job.connector_job_type)}</TableCell>
                    <TableCell>
                      <span className={job.attempt_count > 1 ? 'text-warning-600 font-medium' : ''}>
                        {job.attempt_count}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 dark:text-slate-300">
                      {formatDate(job.started_at)}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 dark:text-slate-300">
                      {formatDate(job.completed_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Link to={`/distribution/books/${job.delivery_id}`}>
                          <Button variant="ghost" size="sm" leftIcon={<EyeIcon className="w-3 h-3" />}>
                            View Book
                          </Button>
                        </Link>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 dark:border-slate-800">
              <div className="text-sm text-slate-600 dark:text-slate-300">
                Page {page} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Error Details for Failed Jobs */}
      {!error && jobs.some(j => j.connector_job_status === 'failed' && j.last_error) && (
        <Card padding="md">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Recent Errors</h3>
          <div className="space-y-3">
            {jobs
              .filter(j => j.connector_job_status === 'failed' && j.last_error)
              .slice(0, 5)
              .map((job) => (
                <div key={job.id} className="p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800/40 rounded-lg">
                  <div className="flex items-start gap-3">
                    <XCircleIcon className="w-5 h-5 text-danger-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-danger-800">
                        Book: {job.delivery_id.slice(0, 8)}...
                      </p>
                      <p className="text-sm text-danger-700 dark:text-danger-200 mt-1 break-words">
                        {job.last_error}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </Card>
      )}
    </div>
  );
}
